const text = (value) => String(value ?? "").trim();

const issue = (step, field, message, severity = "error") => ({
  step,
  field,
  message,
  severity,
});

const firstVersion = (quote) => quote?.versions?.[0] ?? {};

export function quoteWorkflowIssues(
  quote,
  { type = "default", pricing = null, pricingError = null } = {},
) {
  const version = firstVersion(quote);
  const dates = Array.isArray(version?.dates) ? version.dates : [];
  const detailsStep =
    type === "event"
      ? "event-basics"
      : type === "pts"
        ? "pts-basics"
        : "details";
  const datesStep = type === "default" ? "resources" : detailsStep;
  const issues = [];

  if (!text(quote?.reference?.name || quote?.description)) {
    issues.push(issue(detailsStep, "description", "Add a clear quote name."));
  }

  if (!dates.length) {
    issues.push(issue(datesStep, "dates", "Add at least one service date."));
  }

  dates.forEach((date, index) => {
    const label = `Service date ${index + 1}`;
    if (!text(date?.date)) {
      issues.push(
        issue(datesStep, `dates.${index}.date`, `${label} needs a date.`),
      );
    }
    if (!text(date?.start_time)) {
      issues.push(
        issue(
          datesStep,
          `dates.${index}.start_time`,
          `${label} needs a start time.`,
        ),
      );
    }
    if (type !== "pts" && !text(date?.finish_time)) {
      issues.push(
        issue(
          datesStep,
          `dates.${index}.finish_time`,
          `${label} needs a finish time.`,
        ),
      );
    }
    if (
      type === "event" &&
      !text(date?.location_address || date?.location_name)
    ) {
      issues.push(
        issue(
          detailsStep,
          `dates.${index}.location`,
          `${label} needs a venue address.`,
        ),
      );
    }
  });

  if (type === "pts") {
    const risk = version?.pts_risk ?? {};
    [
      ["patient_name", "Add the patient's name."],
      ["pickup_time", "Add the pickup time."],
      ["pickup_address", "Add the pickup address."],
      ["dropoff_address", "Add the drop-off address."],
    ].forEach(([field, message]) => {
      if (!text(risk[field]))
        issues.push(issue(detailsStep, `pts_risk.${field}`, message));
    });
  }

  if (type === "event") {
    const missingFields = text(version?.event_risk?.missing_fields)
      .split(",")
      .map((field) => field.trim())
      .filter(Boolean);
    if (missingFields.length) {
      issues.push(
        issue(
          "event-risk",
          "event_risk.missing_fields",
          `Review missing risk information: ${missingFields.join(", ")}.`,
          "warning",
        ),
      );
    }
  }

  let resourceCount = 0;
  const seenResources = new Set();
  dates.forEach((date, dateIndex) => {
    const resources = Array.isArray(date?.resources) ? date.resources : [];
    resources.forEach((resource, resourceIndex) => {
      const quantity = Number(resource?.quantity);
      if (!Number.isInteger(quantity) || quantity < 1) {
        issues.push(
          issue(
            "resources",
            `dates.${dateIndex}.resources.${resourceIndex}.quantity`,
            `Resource ${resourceIndex + 1} on service date ${dateIndex + 1} needs a quantity of at least 1.`,
          ),
        );
      } else {
        resourceCount += quantity;
      }

      const productId = Number(resource?.product_id ?? resource?.product?.id);
      if (!Number.isFinite(productId) || productId < 1) {
        issues.push(
          issue(
            "resources",
            `dates.${dateIndex}.resources.${resourceIndex}.product_id`,
            `Choose a unit type for resource ${resourceIndex + 1} on service date ${dateIndex + 1}.`,
          ),
        );
        return;
      }

      const duplicateKey = `${dateIndex}:${productId}`;
      if (seenResources.has(duplicateKey)) {
        issues.push(
          issue(
            "resources",
            `dates.${dateIndex}.resources.${resourceIndex}.product_id`,
            `Combine duplicate ${resource?.product?.name ?? "unit"} rows on service date ${dateIndex + 1}.`,
          ),
        );
      }
      seenResources.add(duplicateKey);
    });
  });

  if (resourceCount < 1) {
    issues.push(
      issue(
        "resources",
        "resources",
        "Add at least one unit before reviewing the quote.",
      ),
    );
  }

  if (pricingError) {
    issues.push(issue("review", "pricing", String(pricingError)));
  } else if (!pricing) {
    issues.push(
      issue(
        "review",
        "pricing",
        "Recalculate the price before previewing, accepting, or sending.",
      ),
    );
  }

  return issues;
}

export const blockingIssues = (issues) =>
  (issues ?? []).filter((entry) => entry.severity === "error");

export const issuesForStep = (issues, stepId) =>
  (issues ?? []).filter((entry) => entry.step === stepId);

export function blockingIssuesBeforeStep(issues, steps, targetIndex) {
  const allowedSteps = new Set(
    (steps ?? []).slice(0, Math.max(0, targetIndex)).map((step) => step.id),
  );
  return blockingIssues(issues).filter((entry) => allowedSteps.has(entry.step));
}
